import { Trans } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useSetMeetingContactEmailMutation } from '../../../../graphql/react-operations';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { type GqlMeeting } from '../../../../graphql/base-types.graphql';
import { DateTime } from 'luxon';
import { DateInput } from '@startuptools/ui/custom/DateInput';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';

type FormValues = Pick<GqlMeeting, 'contactEmail'> & { registerAttendanceDate: DateTime | null };

export const GMStepContactEmail = ({
  meeting,
}: {
  meeting: Pick<GqlMeeting, 'id' | 'registerAttendanceDate' | 'contactEmail'>;
}) => {
  const companyId = useCompanyId();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  // eslint-disable-next-line sut/loading-mutation
  const [doSetContactEmail] = useSetMeetingContactEmailMutation();

  const { control, register, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      registerAttendanceDate: meeting.registerAttendanceDate ? DateTime.fromISO(meeting.registerAttendanceDate) : null,
      contactEmail: meeting.contactEmail ?? null,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ registerAttendanceDate, ...values }) => {
    await doSetContactEmail({
      variables: {
        companyId,
        input: {
          id: meeting.id,
          registerAttendanceDate: registerAttendanceDate?.toUTC(0, { keepLocalTime: true }).toISO() ?? null,
          ...values,
        },
      },
    });
    return stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Register Attendance" />
        </Typography>

        <FormControl sx={{ maxWidth: 300 }}>
          <FormLabel>
            <Trans id="Email for registering attendance" />
          </FormLabel>
          <Input {...register('contactEmail')} type="email" />
          <FormHelperText>
            <Trans id="Leave empty if registering is not mandatory" />
          </FormHelperText>
        </FormControl>

        <Controller
          control={control}
          name="registerAttendanceDate"
          render={({ field: { onChange, ...field } }) => (
            <FormControl sx={{ maxWidth: 300 }}>
              <FormLabel>
                <Trans id="Last day to register attendance" />
              </FormLabel>
              <DateInput {...field} onChange={onChange} />
              <FormHelperText>
                <Trans id="Leave empty for no last day" />
              </FormHelperText>
            </FormControl>
          )}
        />

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit">
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
